<template>
  <section v-if="assignment" class="development-detail">
    <header class="development-detail__header">
      <div class="development-detail__identity">
        <span aria-hidden="true" class="development-detail__icon">
          <i class="uil-award"></i>
        </span>
        <div>
          <h1>{{ assignment.name }}</h1>
        </div>
      </div>
      <dl class="development-detail__dates">
        <div>
          <dt>{{ $t('general.assigned_date') }}</dt>
          <dd>{{ formatDate(assignment.assigned_at) }}</dd>
        </div>
        <div>
          <dt>{{ $t('general.updated_date') }}</dt>
          <dd>{{ formatDate(assignment.updated_at) }}</dd>
        </div>
      </dl>
    </header>

    <div class="development-detail__description">
      <h2>{{ $t('general.description') }}</h2>
      <p>
        {{ assignment.description || '' }}
      </p>
      <div v-if="latestTargetText">
        <p>{{ latestTargetText }}</p>
      </div>
    </div>



    <div class="development-detail__comments">
      <div class="development-detail__section-heading">
        <div>
          <h2>{{ $t('general.comments') }}</h2>
          <p v-if="showCommentHint">
            {{ employeeCommentHint }}
          </p>
        </div>
        <span class="development-detail__comment-count">
          {{ commentCount }}
        </span>
      </div>

      <div
          v-if="displayedComments.length"
          class="development-detail__comment-list"
      >
        <article v-for="item in displayedComments"
                 :key="item.id"
                 class="development-comment"
        >
          <UserProfileAvatar :name="commentAuthorName(item)"
                             :photo="commentAuthorImage(item)"
                             class="development-comment-avatar"
          />

          <div class="development-comment__body">
            <div class="development-comment__heading">
              <div>
                <span v-if="commentRoleLabel(item)"
                      class="development-comment__type"
                >
                  {{ commentRoleLabel(item) }}
                </span>
                <strong>{{ commentAuthorName(item) }}</strong>
              </div>
              <time :datetime="item.created_at">
                {{ formatDateTime(item.created_at) }}
              </time>
            </div>
            <p>{{ commentBody(item) }}</p>
            <span v-if="showVisibilityBadge && item.visibility"
                  :class="{
                'development-comment__visibility--public':
                  item.visibility === 'public',
              }"
                  class="development-comment__visibility"
            >
              <i :class="item.visibility === 'public' ? 'uil-eye' : 'uil-eye-slash'"
                 aria-hidden="true"
              ></i>
              {{
                item.visibility === 'public'
                    ? $t('general.visibility_public_badge')
                    : $t('general.visibility_private_badge')
              }}
            </span>
          </div>
        </article>
      </div>

      <div v-else class="development-detail__empty-comments">
        <i aria-hidden="true" class="uil-comment-alt-slash"></i>
        <strong>{{ $t('general.no_visible_comments') }}</strong>
      </div>

      <form v-if="canShowCommentForm"
            class="development-detail__comment-form"
            novalidate
            @submit.prevent="submitComment"
      >
        <label :for="commentInputId">{{ commentFormLabel }}</label>
        <textarea :id="commentInputId"
                  v-model="commentText"
                  :disabled="submitting"
                  :placeholder="commentPlaceholder"
                  rows="4"
                  @input="commentError = ''"
        ></textarea>
        <div class="development-detail__form-footer">
          <span v-if="commentError" class="development-detail__error">
            {{ commentError }}
          </span>
          <div :class="{
              'development-detail__form-actions--visibility':
                canChooseCommentVisibility,
            }"
               class="development-detail__form-actions"
          >
            <multiple-select-input v-if="canChooseCommentVisibility"
                                   v-model="selectedCommentVisibility"
                                   :allowEmpty="false"
                                   :disabled="submitting"
                                   :multiple="false"
                                   :required="false"
                                   :searchable="false"
                                   :showLabels="false"
                                   :values="visibilityOptions"
                                   class="development-detail__select"
                                   openDirection="top"
                                   @select="onVisibilityChange"
            />
            <multiple-select-input v-if="canChooseEntryType"
                                   v-model="selectedEntryType"
                                   :allowEmpty="false"
                                   :disabled="submitting"
                                   :multiple="false"
                                   :required="false"
                                   :searchable="false"
                                   :showLabels="false"
                                   :values="entryTypeOptions"
                                   class="development-detail__select"
                                   openDirection="top"
                                   @select="onEntryTypeChange"
            />
            <button :disabled="submitting || !commentText.trim()" type="submit">
              <i :class="submitting ? 'uil-spinner-alt' : 'uil-message'"
                 aria-hidden="true"/>
              {{ submitting ? submittingLabel : commentSubmitLabel }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </section>

  <div v-else class="development-detail development-detail--empty">
    <i aria-hidden="true" class="uil-award"></i>
    <p>{{ $t('general.load_failed') }}</p>
  </div>
</template>

<script>
import moment from "moment";
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";

/**
 * Shared competency detail UI.
 * Pages own data/API; this component owns presentation by viewer role.
 *
 * employee → my-development
 * manager  → my-team
 * admin    → development-evaluation
 */
const VALID_VIEWERS = ["employee", "manager", "admin"];

export default {
  name: "AssignedCompetencyDetail",
  components: { UserProfileAvatar, MultipleSelectInput },
  props: {
    assignment: {
      type: Object,
      default: null,
    },
    comments: {
      type: Array,
      default: () => [],
    },
    viewer: {
      type: String,
      default: "employee",
      validator: (value) => VALID_VIEWERS.includes(value),
    },
    canComment: {
      type: Boolean,
      default: false,
    },
    allowGoalEntry: {
      type: Boolean,
      default: false,
    },
    allowCommentVisibility: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      commentText: "",
      commentError: "",
      selectedEntryType: null,
      selectedCommentVisibility: null,
    };
  },
  computed: {
    viewerPresets() {
      return {
        employee: {
          formLabel: this.$t("general.employee_comment_form_label"),
          placeholder: this.$t("general.employee_comment_placeholder"),
          submitLabel: this.$t("general.submit_comment"),
          showVisibilityBadge: false,
          showCommentHint: true,
        },
        manager: {
          formLabel: this.$t("general.manager_comment_form_label"),
          updateLabel: this.$t("general.manager_comment_update_label"),
          placeholder: this.$t("general.evaluation_comment_placeholder"),
          submitLabel: this.$t("general.add_comment"),
          showVisibilityBadge: true,
          showCommentHint: false,
        },
        admin: {
          formLabel: this.$t("general.admin_comment_form_label"),
          updateLabel: this.$t("general.admin_comment_update_label"),
          placeholder: this.$t("general.evaluation_comment_placeholder"),
          submitLabel: this.$t("general.add_comment"),
          showVisibilityBadge: true,
          showCommentHint: false,
        },
      };
    },
    preset() {
      return this.viewerPresets[this.viewer] || this.viewerPresets.employee;
    },
    entryTypeOptions() {
      return [
        { id: "comment", name: this.$t("general.comment") },
        { id: "goal", name: this.$t("general.goal") },
      ];
    },
    visibilityOptions() {
      return [
        { id: "public", name: this.$t("general.visibility_public") },
        { id: "private", name: this.$t("general.visibility_private") },
      ];
    },
    entryType() {
      return this.selectedEntryType?.id || "comment";
    },
    commentVisibility() {
      return this.selectedCommentVisibility?.id || "public";
    },
    latestTargetText() {
      const target = this.assignment && this.assignment.latest_target;
      if (!target) return "";
      if (typeof target === "string") return target;
      return target.comment || target.text || "";
    },
    displayedComments() {
      const list = Array.isArray(this.comments) ? this.comments : [];
      return [...list]
        .filter((item) => item && item.id != null)
        .sort((left, right) => {
          const leftTime = Date.parse(left.created_at) || 0;
          const rightTime = Date.parse(right.created_at) || 0;
          return rightTime - leftTime;
        });
    },
    commentCount() {
      if (this.assignment && this.assignment.comments_count != null) {
        return this.assignment.comments_count;
      }
      return this.displayedComments.length;
    },
    hasOwnComment() {
      return !!this.ownNormalComment;
    },
    ownNormalComment() {
      const list = this.displayedComments;
      if (!list.length) return null;

      const authUserId = this.authUser?.id;
      const byUser =
        authUserId != null
          ? list.find(
              (item) =>
                item.type !== "target" &&
                item.user &&
                String(item.user.id) === String(authUserId)
            )
          : null;
      if (byUser) return byUser;

      return (
        list.find(
          (item) => item.type !== "target" && item.can_update === true
        ) || null
      );
    },
    // Normal yorum kişi başına bir tanedir; manager ve admin kendi yorumunu güncelleyebilir
    isUpdatingOwnComment() {
      return (
        (this.viewer === "manager" || this.viewer === "admin") &&
        !this.isGoalEntry &&
        !!this.ownNormalComment
      );
    },
    commentInputId() {
      return `development-${this.viewer}-comment-${this._uid}`;
    },
    // Form her zaman görünür; tekrar yorum denemesinde backend uyarı döner
    canShowCommentForm() {
      return this.canComment;
    },
    canChooseEntryType() {
      return this.viewer === "manager" && this.allowGoalEntry;
    },
    canChooseCommentVisibility() {
      return this.viewer === "admin" && this.allowCommentVisibility;
    },
    showVisibilityBadge() {
      return this.preset.showVisibilityBadge;
    },
    showCommentHint() {
      return this.canComment && this.preset.showCommentHint;
    },
    isGoalEntry() {
      return this.canChooseEntryType && this.entryType === "goal";
    },
    commentFormLabel() {
      if (this.isGoalEntry) return this.$t("general.add_competency_goal");
      if (this.isUpdatingOwnComment) {
        return this.preset.updateLabel || this.preset.formLabel;
      }
      return this.preset.formLabel;
    },
    commentPlaceholder() {
      if (this.isGoalEntry) return this.$t("general.goal_placeholder");
      return this.preset.placeholder;
    },
    commentSubmitLabel() {
      if (this.isGoalEntry) return this.$t("general.save_goal");
      if (this.isUpdatingOwnComment) return this.$t("general.update_comment");
      return this.preset.submitLabel;
    },
    submittingLabel() {
      if (this.isGoalEntry) return this.$t("general.saving");
      return this.isUpdatingOwnComment
        ? this.$t("general.updating")
        : this.$t("general.adding");
    },
    employeeCommentHint() {
      return this.hasOwnComment
        ? this.$t("general.employee_comment_used")
        : this.$t("general.employee_comment_once");
    },
  },
  watch: {
    assignment: {
      immediate: false,
      handler(newValue, oldValue) {
        const nextId = newValue && newValue.id;
        const prevId = oldValue && oldValue.id;
        if (nextId != null && nextId !== prevId) {
          this.resetCommentForm();
        }
      },
    },
    entryType() {
      this.commentError = "";
      this.clearCommentInput();
    },
    submitting(isSubmitting, wasSubmitting) {
      if (wasSubmitting && !isSubmitting) {
        this.clearCommentInput();
      }
    },
    ownNormalComment: {
      immediate: true,
      handler(own) {
        // Textarea'ya yorum metnini yazma; sadece mevcut görünürlüğü seçili getir
        if (own?.visibility && this.canChooseCommentVisibility) {
          this.selectedCommentVisibility =
            this.visibilityOptions.find((option) => option.id === own.visibility) ||
            this.selectedCommentVisibility;
        }
      },
    },
  },
  created() {
    this.selectedEntryType = this.entryTypeOptions[0];
    this.selectedCommentVisibility = this.visibilityOptions[0];
  },
  methods: {
    resetCommentForm() {
      this.commentError = "";
      this.selectedEntryType = this.entryTypeOptions[0];
      this.selectedCommentVisibility = this.visibilityOptions[0];
      this.clearCommentInput();
    },
    onEntryTypeChange(option) {
      this.selectedEntryType = option || this.entryTypeOptions[0];
      this.commentError = "";
    },
    onVisibilityChange(option) {
      this.selectedCommentVisibility = option || this.visibilityOptions[0];
      this.commentError = "";
    },
    submitComment() {
      const value = this.commentText.trim();
      if (!value) {
        this.commentError = this.isGoalEntry
          ? this.$t("general.goal_required")
          : this.$t("general.comment_required");
        return;
      }

      this.$emit(
        "submit-comment",
        value,
        this.isGoalEntry ? "goal" : "comment",
        this.canChooseCommentVisibility ? this.commentVisibility : null
      );
      this.clearCommentInput();
    },
    clearCommentInput() {
      this.commentText = "";
      this.commentError = "";
    },
    commentBody(item) {
      return item.comment || item.text || "";
    },
    commentAuthorName(item) {
      if (item.user) {
        return (
          [item.user.name, item.user.surname].filter(Boolean).join(" ") ||
          this.$t("general.unknown_user")
        );
      }
      return (
        item.author_name ||
        (item.author && item.author.full_name) ||
        this.$t("general.unknown_user")
      );
    },
    commentAuthorImage(item) {
      if (item.user) return item.user.image || item.user.photo_link || "";
      return item.author_image || (item.author && item.author.image) || "";
    },
    commentRoleLabel(item) {
      if (item.role_name) return item.role_name;
      if (item.type === "target") return this.$t("general.goal");
      if (item.author_type === "manager") return this.$t("general.manager_comment");
      if (item.author_type === "employee") return this.$t("general.employee_comment");
      if (item.author_type === "hr") return this.$t("general.hr_comment");
      return item.type === "comment" ? this.$t("general.comment") : "";
    },
    formatDate(value) {
      if (!value) return "-";
      const date = moment(value);
      return date.isValid() ? date.format("DD.MM.YYYY") : "-";
    },
    formatDateTime(value) {
      if (!value) return "-";
      const date = moment(value);
      return date.isValid() ? date.format("DD.MM.YYYY HH:mm") : "-";
    },
  },
};
</script>

<style lang="scss" scoped>
.development-detail {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: visible;
  background: #fff;
  border: 1px solid #e6eaf0;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(37, 48, 68, 0.06);
  box-sizing: border-box;
}

.development-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 26px 28px;
  overflow: hidden;
  background: linear-gradient(135deg, #f7faff 0%, #fff 70%);
  border-bottom: 1px solid #e9edf3;
  border-radius: 12px 12px 0 0;
}

.development-detail__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 16px;

  h1 {
      margin: 0;
      color: #212a39;
      font-size: 22px;
      font-weight: 700;
      overflow-wrap: anywhere;
  }
}

.development-detail__icon {
  display: inline-flex;
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  align-items: center;
  justify-content: center;
  color: #3568d4;
  background: #eaf1ff;
  border-radius: 50%;

  i {
    font-size: 29px;
  }
}

.development-detail__dates {
  display: flex;
  flex: 0 0 auto;
  gap: 26px;
  margin: 0;
  min-width: 0;

  dt {
    margin-bottom: 3px;
    color: #8a94a3;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
  }

  dd {
    margin: 0;
    color: #414c5e;
    font-size: 13px;
    font-weight: 600;
  }
}

.development-detail__description,
.development-detail__target {
  padding: 24px 28px;
  border-bottom: 1px solid #edf0f4;

  h2,
  p {
    margin: 0;
  }

  h2 {
    margin-bottom: 8px;
    color: #293346;
    font-size: 15px;
    font-weight: 700;
  }

  p {
    color: #667286;
    font-size: 13px;
    line-height: 1.65;
  }
}

.development-detail__comments {
  padding: 26px 28px 30px;
}

.development-detail__section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: #293346;
    font-size: 18px;
    font-weight: 700;
  }

  p {
    margin-top: 4px;
    color: #7e8999;
    font-size: 12px;
  }
}

.development-detail__comment-count {
  display: inline-flex;
  min-width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  color: #3568d4;
  background: #eaf1ff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
}

.development-detail__comment-list {
  display: grid;
  gap: 14px;
}

.development-comment {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px;
  background: #fff;
  border: 1px solid #e7ebf0;
  border-radius: 10px;
  box-sizing: border-box;
}

.development-comment > :deep(.avatar) {
  flex: 0 0 44px;
}

.development-comment-avatar {
  width: 44px;
  height: 44px;
  min-width: 44px;
  flex-shrink: 0;
  font-size: 14px;
}

.development-comment__body {
  min-width: 0;
  flex: 1;

  p {
    margin: 9px 0 0;
    color: #566275;
    font-size: 13px;
    line-height: 1.6;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
}

.development-comment__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  strong {
    display: block;
    margin-top: 4px;
    color: #2a3444;
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  time {
    flex: 0 0 auto;
    color: #8b94a3;
    font-size: 11px;
    white-space: nowrap;
  }
}

.development-comment__type {
  display: inline-flex;
  padding: 2px 8px;
  color: #3568d4;
  background: #eaf1ff;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.development-comment__visibility {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  color: #8a94a3;
  font-size: 11px;

  &--public {
    color: #18764c;
  }
}

.development-detail__empty-comments {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 28px 16px;
  color: #8a94a3;
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #dbe3ef;
  border-radius: 10px;

  i {
    font-size: 28px;
  }

  strong {
    color: #526072;
  }
}

.development-detail__comment-form {
  margin-top: 18px;

  label {
    display: block;
    margin-bottom: 8px;
    color: #334155;
    font-size: 13px;
    font-weight: 600;
  }

  textarea {
    width: 100%;
    min-height: 110px;
    padding: 12px 14px;
    border: 1px solid #dbe3ef;
    border-radius: 8px;
    color: #1e293b;
    font: inherit;
    font-size: 14px;
    resize: vertical;
    box-sizing: border-box;

    &:focus {
      border-color: var(--backgroundColor, #666cff);
      outline: 0;
      box-shadow: 0 0 0 3px rgba(102, 108, 255, 0.1);
    }

    &:disabled {
      opacity: 0.7;
      background: #f8fafc;
    }
  }
}

.development-detail__form-footer {
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  min-width: 0;
}

.development-detail__error {
  color: #b42318;
  font-size: 12px;
}

.development-detail__form-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  min-width: 0;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 40px;
    min-height: 40px;
    margin: 0;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    color: #fff;
    background: var(--backgroundColor, #666cff);
    box-shadow: none;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    appearance: none;
    -webkit-appearance: none;

    i {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      line-height: 1;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.development-detail__select {
  flex: 0 0 180px;
  width: 180px;
  max-width: 180px;
  margin: 0;
}

.development-detail__form-actions--visibility .development-detail__select {
  flex-basis: 240px;
  width: 240px;
  max-width: 240px;
}

.development-detail__select :deep(.form-group) {
  position: relative;
  z-index: 1;
  margin: 0;
  width: 100%;
}

.development-detail__select :deep(.multiselect) {
  height: auto !important;
  min-height: 40px !important;
  max-height: none !important;
  overflow: visible !important;
}

.development-detail__select :deep(.multiselect--active) {
  position: relative;
  z-index: 30;
}

.development-detail__select :deep(.multiselect__content-wrapper) {
  z-index: 40;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(30, 41, 59, 0.12);
}

.development-detail__select :deep(.multiselect--above .multiselect__content-wrapper) {
  box-shadow: 0 -10px 24px rgba(30, 41, 59, 0.12);
}

.development-detail__select :deep(.multiselect__tags) {
  box-sizing: border-box;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  padding: 7px 40px 0 12px;
  overflow: hidden;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #fff;
}

.development-detail__select :deep(.multiselect__select) {
  height: 38px;
}

.development-detail__select :deep(.multiselect__placeholder),
.development-detail__select :deep(.multiselect__single) {
  box-sizing: border-box !important;
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  height: 24px !important;
  min-height: 24px !important;
  margin: 0 0 8px !important;
  padding: 0 !important;
  overflow: hidden !important;
  border: 0 !important;
  background: transparent !important;
  color: #334155 !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  line-height: 24px !important;
  white-space: nowrap !important;
  text-overflow: ellipsis !important;
}

.development-detail__select :deep(.multiselect__input) {
  position: absolute !important;
  width: 0 !important;
  min-width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  overflow: hidden !important;
}

.development-detail__select :deep(.multiselect--active .multiselect__tags) {
  border-color: var(--backgroundColor, #666cff);
  box-shadow: 0 0 0 3px rgba(102, 108, 255, 0.1);
}

.development-detail__select :deep(.multiselect--disabled) {
  opacity: 0.7;
}

.development-detail--empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-height: 220px;
  justify-content: center;
  color: #8a94a3;
  text-align: center;

  i {
    font-size: 34px;
  }
}

@media (max-width: 800px) {
  .development-detail__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 18px 16px;
  }

  .development-detail__identity {
    gap: 12px;

    h1 {
      font-size: 17px;
      line-height: 1.35;
    }
  }

  .development-detail__icon {
    width: 46px;
    height: 46px;
    flex-basis: 46px;

    i {
      font-size: 22px;
    }
  }

  .development-detail__dates {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    gap: 12px;
  }

  .development-detail__description,
  .development-detail__target {
    padding: 18px 16px;
  }

  .development-detail__comments {
    padding: 18px 16px 22px;
  }

  .development-detail__section-heading {
    margin-bottom: 14px;

    h2 {
      font-size: 16px;
    }
  }

  .development-comment {
    gap: 10px;
    padding: 14px;
  }

  .development-comment-avatar {
    width: 36px;
    height: 36px;
    min-width: 36px;
    font-size: 12px;
  }

  .development-comment__heading {
    flex-direction: column;
    gap: 4px;

    time {
      white-space: normal;
    }
  }

  .development-detail__comment-form {
    margin-top: 14px;
  }

  .development-detail__form-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    margin-top: 8px;
  }

  .development-detail__form-actions {
    flex-direction: column;
    align-items: stretch;
    margin-left: 0;
    gap: 8px;

    button {
      width: 100%;
      height: 48px;
      min-height: 48px;
      padding: 12px 16px;
      font-size: 14px;
      justify-content: center;
    }
  }

  .development-detail__select,
  .development-detail__form-actions--visibility .development-detail__select {
    flex: 1 1 auto;
    width: 100% !important;
    max-width: none !important;
  }

  .development-detail__select :deep(.multiselect),
  .development-detail__select :deep(.multiselect__tags) {
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
  }

  .development-detail__select :deep(.multiselect__tags) {
    padding: 11px 40px 0 14px;
    border-radius: 10px;
  }

  .development-detail__select :deep(.multiselect__select) {
    height: 46px;
  }

  .development-detail__select :deep(.multiselect__placeholder),
  .development-detail__select :deep(.multiselect__single) {
    height: 26px !important;
    min-height: 26px !important;
    margin-bottom: 8px !important;
    font-size: 16px !important;
    line-height: 26px !important;
  }
}

@media (max-width: 480px) {
  .development-detail__header {
    gap: 12px;
    padding: 14px 12px;
  }

  .development-detail__description,
  .development-detail__target {
    padding: 14px 12px;
  }

  .development-detail__comments {
    padding: 14px 12px 18px;
  }

  .development-detail__dates {
    gap: 8px;
  }

  .development-detail__dates dt {
    font-size: 9px;
  }

  .development-detail__dates dd {
    font-size: 12px;
  }

  .development-comment {
    gap: 8px;
    padding: 12px;
  }

  .development-comment__heading time {
    font-size: 10px;
  }

  .development-detail__comment-form textarea {
    font-size: 16px;
  }
}
</style>
